import { AxiosError } from 'axios';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axiosInstance from 'utils/axiosInterceptors';
import { toastConfig } from 'utils/toastConfig';
import * as yup from 'yup';
import { IndividualCustomerFormDetails } from './CreateIndividualForm';
import { useLocationData } from './useGetLocationData';
import { provinceNameToCodeMap } from 'utils/provinceNameToCode';

type UpdateIndividualFormHandle = {
  submit: () => Promise<boolean | undefined>;
};

export interface UpdateIndividualFormProps {
  customerId?: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  contactNumber: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  barangay: string | null;
  city: string | null;
  province: string | null;
  zipCode: string | null;
  isAMechanic: boolean;
  mechanicType: 'SelfEmployed' | 'CompanyEmployed';
  profession: string | null;
  workCompanyName: string | null;
  membershipCardNumber: string | null;
}

const UpdateIndividualForm = forwardRef<
  UpdateIndividualFormHandle,
  UpdateIndividualFormProps
>(
  (
    {
      customerId,
      firstName,
      lastName,
      email,
      contactNumber,
      addressLine1,
      addressLine2,
      barangay,
      city,
      province,
      zipCode,
      profession,
      isAMechanic,
      workCompanyName,
      mechanicType,
      membershipCardNumber
    },
    ref
  ) => {
    const [individualDetails, setIndividualDetails] =
      useState<UpdateIndividualFormProps>({
        firstName: firstName || '',
        lastName: lastName || '',
        email: email || '',
        contactNumber: contactNumber || '',
        addressLine1: addressLine1 || '',
        addressLine2: addressLine2 || '',
        barangay: barangay || '',
        city: city || '',
        province: province || '',
        zipCode: zipCode || '',
        profession: profession || '',
        isAMechanic: isAMechanic,
        workCompanyName: workCompanyName || '',
        mechanicType: mechanicType,
        membershipCardNumber: membershipCardNumber
      });
    const { provinces, cities, barangays, fetchCities, fetchBarangays } =
      useLocationData();
    const [selectedCity, setSelectedCity] = useState(city || '');

    const handleIndividualFormChange = <
      K extends keyof IndividualCustomerFormDetails
    >(
      field: K,
      value: IndividualCustomerFormDetails[K]
    ) => {
      setIndividualDetails(prev => ({
        ...prev,
        [field]: value
      }));
    };

    const updateIndividualFormSchema = yup.object().shape({
      firstName: yup.string().required('First name is required'),
      lastName: yup.string().required('Last name is required'),
      email: yup.string().email('Invalid email format').required(),
      contactNumber: yup
        .string()
        .matches(/^[0-9]+$/, 'Mobile number must be numeric')
        .required('Mobile number is required'),
      addressLine1: yup.string().required('Address Line 1 is required'),
      addressLine2: yup.string().required('Address Line 2 is required'),
      barangay: yup.string().required('Barangay is required'),
      city: yup.string().required('City is required'),
      province: yup.string().required('Province is required'),
      zipCode: yup
        .string()
        .matches(/^[0-9]+$/, 'Zip Code must be numeric')
        .required('Zip Code is required'),
      profession: yup.string().required('Profession is required'),
      workCompanyName: yup.string().required('Company name is required')
    });

    const handleSubmit = async () => {
      try {
        await updateIndividualFormSchema.validate(individualDetails, {
          abortEarly: false
        });
        await axiosInstance.put(
          `/api/v1/customers/individuals/${customerId}`,
          individualDetails
        );
        toast.success('Customer Updated', { ...toastConfig, autoClose: 2000 });
        return true;
      } catch (error) {
        if (error instanceof yup.ValidationError) {
          error.inner.forEach(err => {
            toast.error(err.message, { ...toastConfig, autoClose: 3000 });
          });
        } else if (error instanceof AxiosError) {
          toast.error(error.response?.data.errorMessage, {
            ...toastConfig,
            autoClose: 3000
          });
        }
      }
      return false;
    };

    useImperativeHandle(ref, () => ({
      submit: handleSubmit
    }));

    useEffect(() => {
      if (province) {
        const provinceCode = provinceNameToCodeMap[province];
        if (provinceCode) {
          fetchCities(provinceCode);
        }
      }
    }, [province]);

    useEffect(() => {
      if (selectedCity) {
        const selectedCityData = cities.find(c => c.name === selectedCity);
        if (selectedCityData) {
          fetchBarangays(selectedCityData.districtCode, selectedCityData.code);
        }
      }
    }, [selectedCity, cities]);

    useEffect(() => {
      if (cities.length > 0 && city) {
        setSelectedCity(city);
      }
    }, [cities]);

    return (
      <Form className="mb-3">
        <Row className="mb-3">
          <Form.Group as={Col}>
            <Form.Label>First Name</Form.Label>
            <Form.Control
              type="text"
              value={individualDetails.firstName || ''}
              onChange={e =>
                handleIndividualFormChange('firstName', e.target.value)
              }
            />
          </Form.Group>
          <Form.Group as={Col}>
            <Form.Label>Last Name</Form.Label>
            <Form.Control
              type="text"
              value={individualDetails.lastName || ''}
              onChange={e =>
                handleIndividualFormChange('lastName', e.target.value)
              }
            />
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col}>
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="text"
              value={individualDetails.email || ''}
              onChange={e =>
                handleIndividualFormChange('email', e.target.value)
              }
            />
          </Form.Group>
          <Form.Group as={Col}>
            <Form.Label>Contact Number</Form.Label>
            <Form.Control
              type="text"
              value={individualDetails.contactNumber || ''}
              onChange={e =>
                handleIndividualFormChange('contactNumber', e.target.value)
              }
            />
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col}>
            <Form.Label>Address Line 1</Form.Label>
            <Form.Control
              type="text"
              value={individualDetails.addressLine1 || ''}
              onChange={e =>
                handleIndividualFormChange('addressLine1', e.target.value)
              }
            />
          </Form.Group>
          <Form.Group as={Col}>
            <Form.Label>Address Line 2</Form.Label>
            <Form.Control
              type="text"
              value={individualDetails.addressLine2 || ''}
              onChange={e =>
                handleIndividualFormChange('addressLine2', e.target.value)
              }
            />
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col}>
            <Form.Label>Province</Form.Label>
            <Form.Control
              as="select"
              value={individualDetails.province || ''}
              onChange={e => {
                const selectedProvinceName = e.target.value;
                const provinceCode =
                  provinceNameToCodeMap[selectedProvinceName];

                if (provinceCode) {
                  handleIndividualFormChange('province', selectedProvinceName);
                  fetchCities(provinceCode);
                }
              }}
            >
              <option value="">Select Province</option>
              {provinces.map((province, index) => (
                <option key={index} value={province.name}>
                  {province.name}
                </option>
              ))}
            </Form.Control>
          </Form.Group>

          <Form.Group as={Col}>
            <Form.Label>City</Form.Label>
            <Form.Control
              as="select"
              value={individualDetails.city || ''}
              onChange={e => {
                const selectedCity = e.target.value;
                const selectedCityDistrictCode = cities.find(
                  city => city.name === selectedCity
                );
                handleIndividualFormChange('city', e.target.value);
                fetchBarangays(
                  selectedCityDistrictCode?.districtCode as string,
                  selectedCityDistrictCode?.code as string
                );
              }}
            >
              <option value="">Select City</option>
              {cities.map((city, index) => (
                <option key={index} value={city.name}>
                  {city.name}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col}>
            <Form.Label>Barangay</Form.Label>
            <Form.Control
              as="select"
              value={individualDetails.barangay || ''}
              onChange={e =>
                handleIndividualFormChange('barangay', e.target.value)
              }
            >
              <option value="">Select Barangay</option>
              {barangays.map((barangay, index) => (
                <option key={index} value={barangay.name}>
                  {barangay.name}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
          <Form.Group as={Col}>
            <Form.Label>Zip Code</Form.Label>
            <Form.Control
              type="text"
              value={individualDetails.zipCode || ''}
              onChange={e =>
                handleIndividualFormChange('zipCode', e.target.value)
              }
            />
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col}>
            <Form.Label>Profession</Form.Label>
            <Form.Control
              type="text"
              value={individualDetails.profession || ''}
              onChange={e =>
                handleIndividualFormChange('profession', e.target.value)
              }
            />
          </Form.Group>
          <Form.Group as={Col}>
            <Form.Label>Work Company Name</Form.Label>
            <Form.Control
              type="text"
              value={individualDetails.workCompanyName || ''}
              onChange={e =>
                handleIndividualFormChange('workCompanyName', e.target.value)
              }
            />
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col}>
            <Form.Label>Is a Mechanic</Form.Label>
            <Form.Control
              as="select"
              value={individualDetails.isAMechanic.toString()}
              onChange={e =>
                handleIndividualFormChange(
                  'isAMechanic',
                  e.target.value === 'true'
                )
              }
            >
              <option value="true">True</option>
              <option value="false">False</option>
            </Form.Control>
          </Form.Group>
          <Form.Group as={Col}>
            <Form.Label>Mechanic Type</Form.Label>
            <Form.Control
              as="select"
              value={individualDetails.mechanicType}
              onChange={e =>
                handleIndividualFormChange(
                  'mechanicType',
                  e.target.value as 'SelfEmployed' | 'CompanyEmployed'
                )
              }
            >
              <option value="SelfEmployed">Self Employed</option>
              <option value="CompanyEmployed">Company Employed</option>
            </Form.Control>
          </Form.Group>
          <Form.Group as={Col}>
            <Form.Label>Membership Card Number (Optional)</Form.Label>
            <Form.Control
              type="text"
              value={individualDetails.membershipCardNumber || ''}
              onChange={e =>
                handleIndividualFormChange('membershipCardNumber', e.target.value)
              }
            />
          </Form.Group>
        </Row>
      </Form>
    );
  }
);

export default UpdateIndividualForm;
